import React, { useState, useEffect, useCallback } from 'react';
import { getUserData, UpdateProfileUser } from '../../../utils/apicall/profileApi';
import { handleError, ApiError } from '../../../utils/error/error';
import NotFound from '../../common/404';
import { UserData } from '../../../utils/types';
import GeneralErrorPage from '../../common/general-error';


const ProfileUserEdit: React.FC = () => {
  const [isValidPath, setIsValidPath] = useState(true);
  const [data, setData] = useState<UserData | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    password_confirmation: '',
  });
  const [modifiedFields, setModifiedFields] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null); // For password mismatch errors

  const loadData = useCallback(async () => {
    try {
      const response = await getUserData();
      if (response) {
        const userData = response.data;
        setData(userData);

        // Prepopulate form with user data
        setFormData({
          name: userData.name || '',
          email: userData.email || '',
          password: '',
          password_confirmation: '',
        });
      } else {
        console.warn('Expected data but got:', response);
      }
    } catch (error) {
      handleError(error);
      if (error instanceof ApiError && error.statusCode === 404) {
        setIsValidPath(false);
      } else {
        console.error('An unexpected error occurred:', error);
        return <GeneralErrorPage />;
      }
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Handle form input changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    setModifiedFields((prev) => new Set(prev).add(name));

    // Real-time password validation
    if (name === 'password' || name === 'password_confirmation') {
      if (
        (name === 'password_confirmation' && formData.password !== value) ||
        (name === 'password' && formData.password_confirmation !== value)
      ) {
        setError('Οι κωδικοί δεν ταιριάζουν. Παρακαλώ δοκιμάστε ξανά.');
      } else {
        setError(null); // Clear the error if they match
      }
    }
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccessMessage(null);
    setErrorMessage(null);
    // Final validation before submission
    if (formData.password !== formData.password_confirmation) {
      setError('Οι κωδικοί δεν ταιριάζουν. Παρακαλώ δοκιμάστε ξανά.');
      return;
    }

    // Prepare the submitted data, excluding 'password_confirmation'
    const submittedData: Partial<typeof formData> = {};



    // Ensure email is always included
    submittedData.name = formData.name;
    submittedData.email = formData.email;
    if  (submittedData.password){
      submittedData.password= formData.email;
    }

    try {
      console.log('Submitting data:', submittedData);
      // Uncomment the following lines to submit data via API and navigate upon success
      const response = await UpdateProfileUser(submittedData);
     if (response) {
      setSuccessMessage('Το προφίλ ενημερώθηκε με επιτυχία!');
     }
    } catch (error) {
      console.error('An error occurred while saving data.', error);
      setErrorMessage('Παρουσιάστηκε σφάλμα κατά την αποθήκευση.');
      setSuccessMessage(null);
    }
  };

  if (!isValidPath) {
    return <NotFound />;
  }

  return (
    <div className="content">
      <section className="ontotita py-5">
        <div className="container mt-4">
          <div className="card p-5">
            <h2 className="h5 mb-2 text-primary">Ενημέρωση Προφίλ</h2>
            <hr className="mb-4" />
            <form onSubmit={handleSubmit} method="POST">
              {successMessage && (
                <div className="alert alert-success alert-dismissible fade show" role="alert">
                  <strong></strong> {successMessage}
                  <button
                    type="button"
                    className="btn-close"
                    data-bs-dismiss="alert"
                    aria-label="Close"
                  ></button>
                </div>
              )}
              {errorMessage && (
                <div className="alert alert-danger alert-dismissible fade show" role="alert">
                  <strong></strong> {errorMessage}
                  <button
                    type="button"
                    className="btn-close"
                    data-bs-dismiss="alert"
                    aria-label="Close"
                  ></button>
                </div>
              )}
              {/* Full Name */}
              <div className="mb-3">
                <label htmlFor="name" className="form-label">
                  Ονοματεπώνυμο
                </label>
                <div className="input-group">
                  <span className="input-group-text">
                    <i className="bi bi-person"></i>
                  </span>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    className="form-control"
                    value={formData.name}
                    onChange={handleChange}
            
                    aria-label="Ονοματεπώνυμο"
                  />
                </div>
              </div>
              {/* Email */}
              <div className="mb-3">
                <label htmlFor="email" className="form-label">
                  Email
                </label>
                <div className="input-group">
                  <span className="input-group-text">
                    <i className="bi bi-envelope"></i>
                  </span>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    className="form-control"
                    value={formData.email}
                    onChange={handleChange}
                  
                    aria-label="Email"
                  />
                </div>
              </div>
              {/* Password Fields */}
              <div className="row mb-3">
                <div className="col-md-6">
                  <label htmlFor="password" className="form-label">
                    Νέος Κωδικός
                  </label>
                  <div className="input-group">
                    <span className="input-group-text">
                      <i className="bi bi-key"></i>
                    </span>
                    <input
                      type="password"
                      id="password"
                      name="password"
                      className="form-control"
                      value={formData.password}
                      onChange={handleChange}
                     
                      aria-label="Νέος Κωδικός"
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <label htmlFor="password_confirmation" className="form-label">
                    Επιβεβαίωση Κωδικού
                  </label>
                  <div className="input-group">
                    <span className="input-group-text">
                      <i className="bi bi-key"></i>
                    </span>
                    <input
                      type="password"
                      id="password_confirmation"
                      name="password_confirmation"
                      className="form-control"
                      value={formData.password_confirmation}
                      onChange={handleChange}
                     
                      aria-label="Επιβεβαίωση Κωδικού"
                    />
                  </div>
                  {error && (
                    <div className="text-danger mt-2">
                      <small>{error}</small>
                    </div>
                  )}
                </div>
              </div>
              {/* Submit Button */}
              <div className="text-end mt-4">
                <button type="submit" className="btn btn-primary">
                  Αποθήκευση Αλλαγών
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ProfileUserEdit;
